import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAssistant } from "@/context/AssistantContext";
import AssistantConversation from "@/components/AssistantConversation";
import "./AssistantDock.scss";

const AssistantDock: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, close, toggle, reset } = useAssistant();
  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  // Focus management: move focus into panel on open, restore on close.
  // wasOpenRef guards against focus-stealing on initial page load.
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
        "input, button, [tabindex]"
      );
      firstFocusable?.focus();
    } else if (wasOpenRef.current) {
      launcherRef.current?.focus();
    }
  }, [isOpen]);

  // Esc closes the dock
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, close]);

  // Focus trap inside panel
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const panel = panelRef.current;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    panel.addEventListener("keydown", handler);
    return () => panel.removeEventListener("keydown", handler);
  }, [isOpen]);

  return (
    <div className="assistant-dock" data-open={isOpen || undefined}>
      {isOpen && (
        <div
          className="dock-panel"
          ref={panelRef}
          role="dialog"
          aria-label={t("assistantDock.panelAriaLabel")}
          aria-modal="true"
        >
          <div className="dock-header">
            <span className="dock-title">{t("assistantDock.panelTitle")}</span>
            <div className="dock-header-actions">
              <button
                type="button"
                className="dock-btn-new"
                onClick={reset}
                aria-label={t("assistantDock.newConversation")}
                title={t("assistantDock.newConversation")}
              >
                ↺
              </button>
              <button
                type="button"
                className="dock-btn-close"
                onClick={close}
                aria-label={t("assistantDock.closeAriaLabel")}
              >
                ✕
              </button>
            </div>
          </div>
          <AssistantConversation surface="dock" />
        </div>
      )}
      <button
        ref={launcherRef}
        type="button"
        className="dock-launcher"
        onClick={toggle}
        aria-label={t(isOpen ? "assistantDock.closeAriaLabel" : "assistantDock.launcherAriaLabel")}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="dock-launcher-icon" aria-hidden="true">
          {isOpen ? "✕" : "✦"}
        </span>
        {!isOpen && (
          <span className="dock-launcher-label">{t("assistantDock.launcherLabel")}</span>
        )}
      </button>
    </div>
  );
};

export default AssistantDock;
